import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import galleryImg1 from "../../assets/galery1.jpg";
import galleryImg2 from "../../assets/galery2.jpg";
import galleryImg3 from "../../assets/galery3.jpg";
import galleryImg4 from "../../assets/galery4.jpg";
import galleryImg5 from "../../assets/galery5.jpg";
import galleryImg6 from "../../assets/galery6.jpg";

import "./HomeComponent.css";
import SubscriberService from "../../core/service/SubscriberService";
import EventService from "../../core/service/EventService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  formatEventDisplayDate,
  getEventSortDate,
  isEventPast,
  isEventWithinMonths,
} from "../../utils/eventDateUtils";

// Static images for cities
import bangaloreImage from "../../assets/BangImg.png";
import hyderabadImage from "../../assets/HydImg.png";
import chennaiImage from "../../assets/CheImg.png";
import puneImage from "../../assets/puneImg.png";

import brochure from "../../assets/CredtechBrochure.pdf";
import srinivasaImage from "../../assets/tesimonialImg1.jpeg";

// Import sound icons
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Import video files
import heroVideoMP4 from "../../assets/bg-video.mp4";
import heroVideoWebM from "../../assets/bg-video.mp4";

const HomePage = () => {
  const subscriberService = useMemo(() => new SubscriberService(), []);
  const eventService = useMemo(() => new EventService(), []);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const setEventsLoading = useState(true)[1];

  const bangaloreRef = useRef(null);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const navigate = useNavigate();

  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  // Video autoplay states
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(false); // Changed to false to start with sound
  const [userInteracted, setUserInteracted] = useState(false); // Track user interaction
  const videoRef = useRef(null);

  const galleryImages = [
    galleryImg1,
    galleryImg2,
    galleryImg3,
    galleryImg4,
    galleryImg5,
    galleryImg6,
  ];

  useEffect(() => {
    const galleryInterval = setInterval(() => {
      setCurrentGalleryIndex(
        (prevIndex) => (prevIndex + 1) % galleryImages.length,
      );
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(galleryInterval);
  }, [galleryImages.length]);

  // Enhanced video autoplay function with sound
  const attemptAutoplay = useCallback(() => {
    const video = videoRef.current;
    if (video && video.paused && !videoError) {
      // First try with sound
      video.muted = false;
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setShowPlayButton(false);
            setVideoError(false);
            setIsMuted(false);
          })
          .catch((error) => {
            // If autoplay with sound fails, try with muted
            console.log("Autoplay with sound failed, trying muted:", error);
            video.muted = true;
            const mutedPlayPromise = video.play();

            if (mutedPlayPromise !== undefined) {
              mutedPlayPromise
                .then(() => {
                  setShowPlayButton(false);
                  setVideoError(false);
                  setIsMuted(true);
                })
                .catch((mutedError) => {
                  setShowPlayButton(true);
                  console.error("Even muted autoplay failed:", mutedError);
                });
            }
          });
      }
    }
  }, [videoError]);

  // Enhanced user interaction detection (without toast messages)
  const handleUserInteraction = useCallback(() => {
    if (!userInteracted) {
      setUserInteracted(true);

      // Try to enable sound on first user interaction
      if (videoRef.current && isMuted) {
        const video = videoRef.current;
        video.muted = false;
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsMuted(false);
            })
            .catch((error) => {
              console.log(
                "Couldn't enable sound even after user interaction:",
                error,
              );
            });
        }
      }
    }
  }, [userInteracted, isMuted]);

  // Add global event listeners to detect user interaction
  useEffect(() => {
    const handleGlobalClick = () => {
      handleUserInteraction();
    };

    const handleKeyPress = () => {
      handleUserInteraction();
    };

    const handleTouchStart = () => {
      handleUserInteraction();
    };

    // Add multiple event listeners to catch any user interaction
    document.addEventListener("click", handleGlobalClick);
    document.addEventListener("keydown", handleKeyPress);
    document.addEventListener("touchstart", handleTouchStart);

    return () => {
      document.removeEventListener("click", handleGlobalClick);
      document.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, [handleUserInteraction]);

  // Video autoplay effect
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Try to autoplay with sound first
    video.muted = false;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    video.disablePictureInPicture = true;
    video.controls = false;

    video.setAttribute("autoplay", "true");
    video.setAttribute("loop", "true");
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("x5-playsinline", "true");
    video.setAttribute("x5-video-player-type", "h5");
    video.setAttribute("data-keepplaying", "true");

    const handleLoadedData = () => {
      setVideoLoaded(true);
      attemptAutoplay();
    };

    const handleCanPlay = () => {
      attemptAutoplay();
    };

    const handlePlay = () => {
      setShowPlayButton(false);
      setVideoError(false);
      // Update sound state based on actual video muted property
      setIsMuted(video.muted);
    };

    const handleError = (e) => {
      setVideoError(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
          setTimeout(attemptAutoplay, 500);
        }
      }, 1000);
    };

    video.addEventListener("loadeddata", handleLoadedData, { once: true });
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("play", handlePlay);
    video.addEventListener("error", handleError);

    attemptAutoplay();

    const timeout1 = setTimeout(attemptAutoplay, 300);
    const timeout2 = setTimeout(attemptAutoplay, 1000);
    const timeout3 = setTimeout(attemptAutoplay, 2000);

    video.load();

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("error", handleError);
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [attemptAutoplay, videoError]);

  // Update video muted state when isMuted changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Handle play button click
  const handlePlayClick = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      // Try to play with sound
      video.muted = false;
      video
        .play()
        .then(() => {
          setShowPlayButton(false);
          setVideoError(false);
          setUserInteracted(true);
          setIsMuted(false);
        })
        .catch((error) => {
          console.error("Manual play with sound failed:", error);
          // Fall back to muted play
          video.muted = true;
          video
            .play()
            .then(() => {
              setShowPlayButton(false);
              setVideoError(false);
              setUserInteracted(true);
              setIsMuted(true);
            })
            .catch((mutedError) => {
              console.error("Manual play failed:", mutedError);
            });
        });
    }
  }, []);

  // Toggle sound function (without toast messages)
  const toggleSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // If user hasn't interacted yet, try to enable sound
    if (!userInteracted) {
      setUserInteracted(true);
    }

    const newMutedState = !isMuted;

    // Try to change muted state
    video.muted = newMutedState;

    // Check if we successfully changed the muted state
    setTimeout(() => {
      if (video.muted === newMutedState) {
        setIsMuted(newMutedState);
      }
    }, 100);
  }, [isMuted, userInteracted]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: false,
    dotsClass: "slick-dots custom-dots", // We will style this class specifically
  };

  // Testimonials state
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  const heroTitles = [
    "Sustainability",
    "Best Practices",
    "Industry Growth",
    "AI Impact",
    "Diversity",
    "100+ Participants",
    "Facility Management",
    "Corporate Real Estate",
    "Architects",
    "Developers",
    "Workplace Strategists",
    "Founders",
    "CXOs",
    "Startup Leaders",
    "4 Cities",
    "14+ Platforms",
    "Real Estate Startups",
  ];

  const journeyVideosHome = [
    "https://youtu.be/ROSqrTX9d9o?si=QUsq8BYHkOzIdmhf",
    "https://youtu.be/3FmOrF99yOQ?si=tCyZ8zO5JT6gGAGk",
    "https://youtu.be/RwVrsl3p5CU?si=-qj_zY9hnMAcpc0B",
    "https://youtu.be/Vo2U9SRwHt0?si=ahHRdXxHrC8GuoNR",
    "https://youtu.be/BweKJFa4b8g?si=xpz1kXG4Mytb5pka",
    "https://youtu.be/FtamU1FTvq0?si=DPz139ixrXqmzXgw",
    "https://youtu.be/Iogl0ImqMFI?feature=shared",
    "https://youtu.be/KYIXpNFtKN0?feature=shared",
    "https://youtu.be/iQlwE-L24fg?si=q3UOyBhzvEhBpSA-",
    "https://youtu.be/ZxPfSiK1pZg?si=kJcuo3dD-_a5HVFO",
    "https://youtu.be/fKEfs4H2els?si=mNoK8qNX5zRIzUI2",
  ];

  // Testimonials data
  const testimonials = [
    {
      text: "Firstly, I would like to thank CREDTECH for inviting me and for organizing such a wonderful event that brought together some of the senior-most leaders of industry. Engaging with industry leaders and exchanging perspectives offered valuable insights and a vision for the evolving GCC landscape in India. I'll be more than happy to join such enriching events across India to share experiences and learnings with the industry.",
      author: "Mr. Srinivasa Prasad Vemuru",
      designation: "Senior Director - Facilities, India at OpenText",
      image: srinivasaImage,
    },
    {
      text: "Thank you to CREDTECH and the wonderful people at the CREDTECH Knowledge Platform – Hyderabad Edition for a day of learning and sharing.",
      author: "Gokhana",
    },
    {
      text: "Our CEO, Ankur Marda, enjoyed the warm Hyderabad welcome and was truly energised by the panel discussion with other distinguished participants on Next Gen Workspaces.",
      author: "Gokhana",
    },
    {
      text: "Thank you, CREDTECH, for highlighting impactful leadership and curating a platform that fosters forward-thinking dialogue in commercial real estate and procurement.",
      author: "Encore",
    },
    {
      text: "CREDTECH 2025 – Pune was an incredible event at the Ritz Carlton, a great platform for networking, knowledge sharing, and interacting with industry veterans. Grateful to be part of such engaging conversations and a vibrant community!",
      author: "Yogesh Sharma, Coditas",
    },
    {
      text: "Just wrapped up an incredible event hosted by CREDTECH — an inspiring lineup of speakers, insightful discussions on EVs, GCC growth, and Vision 2030, and the chance to interact with industry veterans. Grateful for the engaging conversations, vibrant community, and warm invitation from Sunanda Jaiprakash.",
      author: "Jyothi Patil",
    },
    {
      text: "Proud to be part of CREDTECH Pune 2025, where our MD, Alok Kumar, shared insights on the evolving GCC landscape and India's next growth story driven by startups and SMEs. Kudos to CREDTECH and Sunanda Jaiprakash for curating such a meaningful platform that fosters impactful dialogue.",
      author: "SRKay Consulting Group",
    },
  ];

  const nextTitle = useCallback(() => {
    setCurrentTitleIndex((prevIndex) => (prevIndex + 1) % heroTitles.length);
  }, [heroTitles.length]);

  const handleDownloadBrochure = () => {
    const link = document.createElement("a");
    link.href = brochure;
    link.download = "CREDTECH-Brochure.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Testimonial navigation functions
  const goToPreviousTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1,
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex(
      (prevIndex) => (prevIndex + 1) % testimonials.length,
    );
  };

  useEffect(() => {
    const titleInterval = setInterval(() => {
      nextTitle();
    }, 2000);

    return () => clearInterval(titleInterval);
  }, [nextTitle]);

  useEffect(() => {
    // Fetch all events - Updated to match PlatformPage logic
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const response = await eventService.getAllEvents();
        if (response.data && response.data.responseBody) {
          const sortedEvents = [...response.data.responseBody].sort(
            (a, b) => getEventSortDate(a) - getEventSortDate(b),
          );
          setEvents(sortedEvents);
        }
      } catch (error) {
        toast.error("Failed to load events");
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [eventService]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await subscriberService.subscribe({ email });

      if (response.data && response.data.responseMessage) {
        toast.success(response.data.responseMessage);
      } else {
        toast.success("Subscribed successfully!");
      }
      setEmail("");
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.responseMessage
      ) {
        toast.error(error.response.data.responseMessage);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const upcomingEvents = events.filter((event) => !isEventPast(event));

  const findNextUpcomingEvent = () => {
    if (!upcomingEvents.length) return null;
    const sortedEvents = [...upcomingEvents].sort(
      (a, b) => getEventSortDate(a) - getEventSortDate(b),
    );
    return sortedEvents[0];
  };

  const nextEvent = findNextUpcomingEvent();

  const hasEventWithinOneMonth = () => {
    if (!upcomingEvents.length) return false;
    return upcomingEvents.some((event) => isEventWithinMonths(event, 3));
  };

  // Get static image for city
  const getCityImage = (city) => {
    switch (city.toLowerCase()) {
      case "pune":
        return puneImage;
      case "bangalore":
        return bangaloreImage;
      case "hyderabad":
        return hyderabadImage;
      case "chennai":
        return chennaiImage;
      default:
        return bangaloreImage; // Default image
    }
  };

  const handleRegisterClick = (event) => {
    navigate("/register-now", {
      state: {
        city: event.city,
        date: formatEventDisplayDate(event),
        image: getCityImage(event.city),
        euid: event.euid || "", // Include euid for token generation
      },
    });
  };

  // Handle upcoming button click (for events beyond 1 month)
  const handleUpcomingClick = (event) => {
    toast.info(`Registration for ${event.city} event will open soon!`);
  };

  const goToNextEventRegister = () => {
    if (!nextEvent) return;

    // Check if there are any events within one month
    if (hasEventWithinOneMonth()) {
      navigate("/register-now", {
        state: {
          city: nextEvent.city,
          date: formatEventDisplayDate(nextEvent),
          image: getCityImage(nextEvent.city), // use local mapping
          euid: nextEvent.euid || "",
        },
      });
    } else {
      // Navigate to the platforms page instead of scrolling to a section
      navigate("/platforms");
    }
  };

  // Function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (url.includes("embed/")) {
      return url;
    }

    let videoId = "";

    if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(new URL(url).search);
      videoId = urlParams.get("v");
    } else if (url.includes("youtube.com/shorts/")) {
      videoId = url.split("shorts/")[1]?.split("?")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }

    return url;
  };

  return (
    <div className="home-page">
      <div className="gradient-wrapper">
        {/* Hero section with video background */}
        <div className="hero-section-home">
          <video
            ref={videoRef}
            className={`hero-video ${videoLoaded ? "loaded" : ""}`}
            style={{
              display: videoError ? "none" : "block",
              opacity: videoLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
            poster={require("../../assets/home-bg.jpg")}
            autoPlay
            muted={isMuted}
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            controls={false}
          >
            <source src={heroVideoWebM} type="video/webm" />
            <source src={heroVideoMP4} type="video/mp4" />
            Your browser does not support video tag.
          </video>

          {!videoLoaded && !videoError && (
            <div
              className="video-poster-overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundImage: `url(${require("../../assets/home-bg.jpg")})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "brightness(0.7)",
                zIndex: 1,
              }}
            />
          )}

          {showPlayButton && (
            <div className="video-play-overlay" onClick={handlePlayClick}>
              <div className="play-button">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  width="48px"
                  height="48px"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <p>Click to play video</p>
            </div>
          )}

          {!videoLoaded && !videoError && (
            <div className="video-loading">
              <div className="loading-spinner"></div>
              <p>Loading video...</p>
            </div>
          )}

          {videoError && (
            <div className="video-error">
              <p>Failed to load video. Please refresh the page.</p>
              <button
                className="retry-button"
                onClick={() => {
                  setVideoError(false);
                  if (videoRef.current) {
                    videoRef.current.load();
                    setTimeout(() => {
                      if (videoRef.current) {
                        videoRef.current
                          .play()
                          .catch((e) => console.error("Retry failed:", e));
                      }
                    }, 500);
                  }
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Sound Control Button */}
          <button
            className="sound-control-btn"
            onClick={toggleSound}
            title={isMuted ? "Click to enable sound" : "Click to mute video"}
          >
            {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
          </button>

          <div className="hero-overlay-home">
            <div className="hero-content-home">
              <div className="hero-main-content-home">
                <div className="hero-text-section-home"></div>
              </div>
            </div>
          </div>
        </div>

        {/*Presenting CREDTECH section */}
        <div className="presenting-credtech-section">
          <h2 className="presenting-credtech-title">
            Presenting the CREDTECH Chennai Edition on 21 August 2026
          </h2>
          <h3 className="presenting-credtech-subtitle">
            Theme for 2026: Shaping the Future: Insights on GCC and Ecosystem
            Growth
          </h3>
          <p className="presenting-credtech-description">
            The CREDTECH Chennai Edition brings together senior leaders across
            Corporate Real Estate, GCCs, occupiers, facilities, procurement,
            developers, architects, and workplace strategists to explore the
            evolving landscape of GCC growth, workplace transformation, and
            enterprise infrastructure.
            <br />
            <br />
            Chennai continues to strengthen its position within India’s GCC
            ecosystem through its established business environment, strong
            engineering and technology talent base, expanding office
            infrastructure, and growing presence of global enterprises across
            sectors including technology, manufacturing, automotive, and
            financial services. As organisations increasingly look at long-term
            capability building and operational resilience, the city remains an
            important destination for GCC expansion and strategic investments.
            <br />
            <br />
            The second panel discussion will focus on Smart Office Innovations
            and Intelligent Infrastructure, exploring how data-driven
            workplaces, AI-enabled facilities management, sustainable
            infrastructure, workplace automation, and hybrid work models are
            transforming offices into more efficient, adaptable, and
            employee-focused environments that support productivity,
            collaboration, and long-term business growth.
          </p>
        </div>

        {/* Updated Upcoming Platforms Section - Now using the same logic as PlatformPage */}
        <div className="city-section">
          <h2 className="section-title-home">Upcoming Platforms</h2>
          <div className="gallery-scroll-container-new" ref={bangaloreRef}>
            {upcomingEvents.map((event) => (
              <div className="gallery-item-new" key={event.id}>
                <div className="platform-info-new">
                  <div className="platform-details-new">
                    <span className="platform-city-new">
                      {event.city} Chapter
                    </span>
                    <span className="platform-date-new">
                      |{" "}
                      {formatEventDisplayDate(event)}
                    </span>
                  </div>

                  {isEventWithinMonths(event, 3) ? (
                    <button
                      className="platform-button-new register-button"
                      onClick={() => handleRegisterClick(event)}
                    >
                      Register Now
                    </button>
                  ) : (
                    <button
                      className="platform-button-new upcoming-button"
                      onClick={() => handleUpcomingClick(event)}
                    >
                      Upcoming
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="download-brochure">
            <button
              className="brochure-button"
              onClick={handleDownloadBrochure}
            >
              Download Brochure →
            </button>
          </div>
        </div>

        {/* About CREDTECH Section remains the same */}
        <div className="about-credtech-section">
          <h2 className="about-credtech-title">About CREDTECH</h2>
          <h4>Our Vision</h4>
          <p className="about-description">
            A platform for business visionaries to shape the future of
            workspaces — powered by insights across GCC leadership, industry
            growth, AI, design thinking, smart workplaces, wellness,
            sustainability and emerging best practices.
          </p>
          <h4>Our Presence</h4>
          <p className="about-description">
            Founded in 2020, CREDTECH has emerged as a premier knowledge-sharing
            and networking platform. CREDTECH serves as a forum for dialogue,
            strategic thinking, and collaboration—bringing together leaders
            shaping business growth, workplace transformation, and innovation.
            Across 15+ editions, each session has convened 100+ senior leaders
            from Commercial Real Estate, Facilities, GCCs, Architecture,
            Workplace Strategy, procurement, and emerging startups to
            collectively reimagine the future of workspaces. Today, the platform
            spans four key cities—Bangalore, Hyderabad, Pune, and Chennai.
          </p>
        </div>

        {/* --- Gallery Slideshow Section --- */}
        <div className="gallery-static-section-home">
          <h2 className="static-section-title-home">Gallery</h2>

          <div className="gallery-image-container-home">
            <img
              key={currentGalleryIndex}
              src={galleryImages[currentGalleryIndex]}
              alt={`Gallery Slide ${currentGalleryIndex + 1}`}
              className="gallery-static-image-home fade-animation"
            />
          </div>
        </div>

        {/* --- TESTIMONIALS SECTION --- */}
        <div className="testimonials-section-home">
          {/* 1. HEADER (Hashtag + Title) */}
          <div className="testimonials-header-home">
            <span className="hashtag-home">#Testimonials</span>
            <h2 className="main-heading-home">
              Reflections From The Visionaries Of Our Community
            </h2>
          </div>

          {/* 2. CAROUSEL (Vertical Video Only) */}
          <div className="carousel-wrapper-home">
            <Slider {...settings}>
              {/* Slide 1 */}
              <div className="video-slide-home">
                <iframe
                  className="vertical-video-home"
                  src={getYouTubeEmbedUrl(
                    "https://youtube.com/shorts/yzBedBRG7UQ?feature=shared",
                  )}
                  title="Testimonial 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Slide 2 */}
              <div className="video-slide-home">
                <iframe
                  className="vertical-video-home"
                  src={getYouTubeEmbedUrl(
                    "https://youtube.com/shorts/Dq7unkcwcQQ?feature=shared",
                  )}
                  title="Testimonial 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Slide 3 */}
              <div className="video-slide-home">
                <iframe
                  className="vertical-video-home"
                  src={getYouTubeEmbedUrl(
                    "https://youtube.com/shorts/ly-cCFbUaAo?feature=shared",
                  )}
                  title="Testimonial 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Slider>
          </div>
        </div>

        {/* --- Our Journey Since 2020 --- */}
        <div className="journey-section-home">
          <h2 className="journey-title-home">Our Journey Since 2020</h2>

          <div className="journey-container-home">
            {journeyVideosHome.map((url, index) => (
              <div className="journey-video-card-home" key={index}>
                <div className="video-embed-wrapper-home">
                  <iframe
                    className="journey-video-home"
                    src={getYouTubeEmbedUrl(url)}
                    title={`Journey Video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="newsletter-section">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Subscribe Here</h2>
            <h2 className="avail-updates">To avail more updates</h2>
            <>
              <form className="newsletter-form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  className="newsletter-input"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ color: "white" }}
                  required
                />
                <button
                  type="submit"
                  className="newsletter-button"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </form>

              <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
              />
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
